import { useCanvasFileRecords } from "@/api/fileRecords";
import { useCanvasNodesMirrorState } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import { useLocalStorageState } from "ahooks";
import { useMemo } from "react";
import {
  type ContentCategory,
  type ContentsKind,
  type ContentsSort,
  buildAllContentItems,
  buildFileRecordLookups,
  buildSectionView,
  buildVisibleItemIdOrder,
  filterVisibleItems,
  groupItemsByType,
  kindForGroupKey,
  sortContentItems,
} from "./canvasRailContentsModel";

export function useCanvasRailContents(
  canvasId: string,
  searchQuery: string,
  options?: { filesOnly?: boolean },
) {
  // One lens (DS 021): the view-mode fork (section vs type) retired with
  // the drawer restructure — the type folders below ARE the organization,
  // with Sections leading. Sort orders WITHIN a folder.
  const [sortPref, setSort] = useLocalStorageState<ContentsSort>(
    "canvas-explorer.canvas.contents.sort",
    { defaultValue: "layer" },
  );
  const sort: ContentsSort = sortPref ?? "layer";

  // Show hides whole FOLDERS (DS 021), not item categories — everything
  // is visible by default and a hidden kind is one closed door.
  const [hiddenKindsPref, setHiddenKinds] = useLocalStorageState<
    ContentsKind[]
  >("canvas-explorer.canvas.contents.hidden", { defaultValue: [] });
  const hiddenKinds = useMemo(
    () => new Set<ContentsKind>(hiddenKindsPref ?? []),
    [hiddenKindsPref],
  );

  const toggleKind = (kind: ContentsKind) => {
    const current = new Set<ContentsKind>(hiddenKindsPref ?? []);
    if (current.has(kind)) current.delete(kind);
    else current.add(kind);
    setHiddenKinds(Array.from(current));
  };

  const activeFilter = useMemo(() => {
    if (options?.filesOnly) return new Set<ContentCategory>(["file"]);
    return new Set<ContentCategory>(["file", "entity", "object"]);
  }, [options?.filesOnly]);

  const {
    data: fileRecords,
    isLoading: filesLoading,
    isFetched: filesFetched,
  } = useCanvasFileRecords(canvasId || undefined);

  const { nodes: rawNodes, nodesSynced } = useCanvasNodesMirrorState(canvasId);

  const resolvedFileRecords = fileRecords ?? [];
  const resolvedRawNodes = rawNodes;

  const isContentsLoading =
    !!canvasId &&
    (!filesFetched ||
      !nodesSynced ||
      (filesLoading && resolvedFileRecords.length === 0));

  const { fileRecordIds, nodesById, fileRecordIdToNodeId, fileRecordNodeIds } =
    useMemo(
      () => buildFileRecordLookups(resolvedFileRecords, resolvedRawNodes),
      [resolvedFileRecords, resolvedRawNodes],
    );

  const allItems = useMemo(
    () =>
      buildAllContentItems({
        fileRecords: resolvedFileRecords,
        rawNodes: resolvedRawNodes,
        fileRecordIdToNodeId,
        fileRecordNodeIds,
        nodesById,
      }),
    [
      resolvedFileRecords,
      resolvedRawNodes,
      fileRecordIdToNodeId,
      fileRecordNodeIds,
      nodesById,
    ],
  );

  const visibleItems = useMemo(
    () => filterVisibleItems(allItems, activeFilter, searchQuery),
    [allItems, activeFilter, searchQuery],
  );

  const visibleTotal = visibleItems.length;

  const sectionView = useMemo(() => {
    const built = buildSectionView({
      rawNodes: resolvedRawNodes,
      visibleItems,
      searchQuery,
    });
    return {
      ...built,
      entries: built.entries.map((e) => ({
        ...e,
        items: sortContentItems(e.items, sort),
      })),
    };
  }, [resolvedRawNodes, visibleItems, searchQuery, sort]);

  // The unsectioned remainder, foldered by type — with the section items
  // above, this IS the one-lens body. Hidden kinds drop their folders here.
  const rootTypeGroups = useMemo(
    () =>
      groupItemsByType(sectionView.rootItems)
        .filter((g) => !hiddenKinds.has(kindForGroupKey(g.key)))
        .map((g) => ({
          ...g,
          items: sortContentItems(g.items, sort),
        })),
    [sectionView.rootItems, sort, hiddenKinds],
  );

  const sectionsHidden = hiddenKinds.has("sections");

  const headerCount = sectionView.totalVisible;

  const hasBody =
    sectionView.entries.length > 0 || sectionView.rootItems.length > 0;

  // Range-select follows the VISIBLE order — hidden folders' items are not
  // selectable ghosts.
  const visibleItemIdOrder = useMemo(
    () =>
      buildVisibleItemIdOrder({
        sectionView: sectionsHidden
          ? { ...sectionView, entries: [] }
          : sectionView,
        rootTypeGroups,
      }),
    [sectionView, rootTypeGroups, sectionsHidden],
  );

  const isEmpty =
    !isContentsLoading && allItems.length === 0 && searchQuery.length === 0;

  return {
    searchQuery,
    sort,
    setSort,
    hiddenKinds,
    toggleKind,
    sectionsHidden,
    activeFilter,
    fileRecords: resolvedFileRecords,
    rawNodes: resolvedRawNodes,
    fileRecordIds,
    nodesById,
    fileRecordIdToNodeId,
    fileRecordNodeIds,
    allItems,
    visibleItems,
    visibleTotal,
    rootTypeGroups,
    sectionView,
    headerCount,
    hasBody,
    visibleItemIdOrder,
    isContentsLoading,
    isEmpty,
  };
}
